"use client";

import { Locale } from "@/types/locale";
import React from "react";
import { cn } from "@lib/utils";
import { ProgramProposalEntity } from "@features/events-program/types/program-proposal";
import { AssignmentMemberDisplayLookup } from "@features/events-program/ui/assigments/assignment-member-display";
import { getProposalRatingResolutionColor, ProposalVoteDisplay } from "./proposal-vote-display";
import {
  ProposalVoteAction,
  ProposalVoteActions,
  ProposalVoteDetailsVisibility,
  ProposalVotesHoverCard,
} from "./proposal-votes-hover-card";
import { ProposalVotesHiddenContentLabels } from "./proposal-votes-hidden-content";

type VotingPanelRatingCellHiddenLabels = ProposalVotesHiddenContentLabels & {
  ratingAriaLabel: string;
};

type VotingPanelRatingCellProps = {
  ratingText: string;
  proposal: ProgramProposalEntity;
  locale: Locale;
  voteDisplayLookup?: AssignmentMemberDisplayLookup;
  currentTeamMemberIds?: readonly number[];
  emptyValue?: string;
  detailsVisibility?: ProposalVoteDetailsVisibility;
  hiddenLabels?: VotingPanelRatingCellHiddenLabels;
  voteActions?: ProposalVoteActions;
  voteAction?: ProposalVoteAction;
  onEditVote?: (voterId: number) => void;
  canEditVote?: (vote: ProposalVoteDisplay) => boolean;
  averageRatingText?: string;
  onOpenProposal?: () => void;
};

export const VotingPanelRatingCell = React.memo<VotingPanelRatingCellProps>(
  ({
    ratingText,
    proposal,
    locale,
    voteDisplayLookup,
    currentTeamMemberIds,
    emptyValue = "-",
    detailsVisibility,
    hiddenLabels,
    voteActions,
    voteAction,
    onEditVote,
    canEditVote,
    averageRatingText,
    onOpenProposal,
  }) => {
    const isEmpty = ratingText === emptyValue;
    const ratingHidden = detailsVisibility?.ratingHidden === true && !isEmpty;
    const ratingColorClassName = isEmpty
      ? "text-muted-foreground"
      : ratingHidden
        ? "text-muted-foreground"
        : getProposalRatingResolutionColor(proposal.votes).className;
    const trigger = (
      <button
        type="button"
        aria-label={ratingHidden ? hiddenLabels?.ratingAriaLabel : undefined}
        onClick={onOpenProposal}
        className={cn(
          "block w-full rounded-md px-1.5 py-1 text-right text-base leading-none font-semibold tabular-nums transition-colors hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
          ratingColorClassName,
          ratingHidden && "bg-muted/60 text-transparent hover:bg-muted/60",
        )}
      >
        <span aria-hidden={ratingHidden ? "true" : undefined}>{ratingText}</span>
      </button>
    );

    return (
      <ProposalVotesHoverCard
        proposal={proposal}
        locale={locale}
        voteDisplayLookup={voteDisplayLookup}
        currentTeamMemberIds={currentTeamMemberIds}
        trigger={trigger}
        detailsVisibility={detailsVisibility}
        hiddenLabels={hiddenLabels}
        voteActions={voteActions}
        voteAction={voteAction}
        onEditVote={onEditVote}
        canEditVote={canEditVote}
        averageRatingText={averageRatingText}
        onOpenProposal={onOpenProposal}
      />
    );
  },
);

VotingPanelRatingCell.displayName = "VotingPanelRatingCell";
